import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Storefront, Package, Receipt, ArrowsClockwise, Plus, ArrowSquareOut, CheckCircle, Warning, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const FIELDS = {
  shopify: [
    { key: "shop_domain", label: "Shop domain", ph: "myshop.myshopify.com" },
    { key: "api_key", label: "Admin API access token", ph: "shpat_…", secret: true },
  ],
  woocommerce: [
    { key: "store_url", label: "Store URL (HTTPS)", ph: "https://store.com" },
    { key: "api_key", label: "Consumer key", ph: "ck_…" },
    { key: "consumer_secret", label: "Consumer secret", ph: "cs_…", secret: true },
  ],
  etsy: [
    { key: "keystring", label: "API Keystring (Client ID)", ph: "abc123…", secret: true },
    { key: "shared_secret", label: "Shared Secret", ph: "shared secret…", secret: true },
  ],
  cgtrader: [
    { key: "client_id", label: "Client ID", ph: "cgtrader client id…", secret: true },
    { key: "client_secret", label: "Client Secret", ph: "cgtrader client secret…", secret: true },
  ],
  "3dartix": [
    { key: "api_key", label: "API Key", ph: "3DArtiX API key…", secret: true },
  ],
};

export default function Ecommerce() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [stats, setStats] = useState({ products: 0, orders: 0, by_platform: [] });
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [syncing, setSyncing] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ connector_id: "shopify", name: "", secrets: {} });
  const [revealed, setRevealed] = useState({});

  const load = () => {
    api.get("/ecommerce/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/ecommerce/stores").then((r) => setStores(r.data)).catch(() => {});
    api.get("/ecommerce/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/ecommerce/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("oauth") === "connected") {
      toast.success(t("ecom.storeConnected"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p.get("oauth") === "error") {
      toast.error(t("ecom.syncFailed"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectStore = async () => {
    try {
      if (form.connector_id === "etsy") {
        if (!(form.secrets.keystring || "").trim()) { toast.error(t("ecom.keystringRequired")); return; }
        if (!(form.secrets.shared_secret || "").trim()) { toast.error(t("ecom.sharedSecretRequired")); return; }
        const { data: conn } = await api.post("/integrations/connections", { connector_id: "etsy", name: form.name || "Etsy", secrets: form.secrets });
        const { data } = await api.post(`/integrations/connections/${conn.id}/oauth/start`);
        window.location.href = data.authorize_url;
        return;
      }
      if (form.connector_id === "cgtrader") {
        if (!(form.secrets.client_id || "").trim()) { toast.error(t("ecom.cgtraderClientIdRequired")); return; }
        if (!(form.secrets.client_secret || "").trim()) { toast.error(t("ecom.cgtraderClientSecretRequired")); return; }
        const { data: conn } = await api.post("/integrations/connections", { connector_id: "cgtrader", name: form.name || "CGTrader", secrets: form.secrets });
        const { data } = await api.post(`/integrations/connections/${conn.id}/oauth/start`);
        window.location.href = data.authorize_url;
        return;
      }
      await api.post("/integrations/connections", { connector_id: form.connector_id, name: form.name || form.connector_id, secrets: form.secrets });
      setOpen(false); setForm({ connector_id: "shopify", name: "", secrets: {} }); load();
      toast.success(t("ecom.storeConnected"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const syncStore = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/integrations/connections/${id}/sync`);
      if (data.status === "completed") toast.success(t("ecom.syncedRecords", { n: data.records }));
      else toast.error(`${t("ecom.syncFailed")}: ${data.error || ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(""); }
  };

  const StatCard = ({ Icon, slug, label, value }) => (
    <div className="bg-card border border-border rounded-sm p-5" data-testid={`ecom-stat-${slug}`}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon size={16} /><span className="overline text-xs">{label}</span></div>
      <div className="font-heading text-3xl font-bold">{value}</div>
    </div>
  );

  return (
    <div data-testid="ecommerce-page">
      <PageHeader title={t("ecom.title")} subtitle={t("ecom.subtitle")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-sm" data-testid="connect-store-button"><Plus size={16} className="mr-2" />{t("ecom.connectStore")}</Button></DialogTrigger>
            <DialogContent className="backdrop-blur-xl bg-background/90">
              <DialogHeader><DialogTitle className="font-heading">{t("ecom.connectStore")}</DialogTitle>
                <DialogDescription>{t("ecom.connectDesc")}</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-2"><Label>{t("ecom.platform")}</Label>
                  <Select value={form.connector_id} onValueChange={(v) => setForm({ connector_id: v, name: "", secrets: {} })}>
                    <SelectTrigger className="rounded-sm" data-testid="store-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="shopify">Shopify</SelectItem><SelectItem value="woocommerce">WooCommerce</SelectItem><SelectItem value="etsy">Etsy</SelectItem><SelectItem value="cgtrader">CGTrader</SelectItem><SelectItem value="3dartix">3DArtiX</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>{t("common.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="store-name-input" /></div>
                {FIELDS[form.connector_id].map((f) => (
                  <div className="space-y-2" key={f.key}><Label>{f.label}</Label>
                    <div className="relative">
                      <Input type={f.secret && !revealed[f.key] ? "password" : "text"} placeholder={f.ph}
                        value={form.secrets[f.key] || ""} data-testid={`store-secret-${f.key}`}
                        onChange={(e) => setForm({ ...form, secrets: { ...form.secrets, [f.key]: e.target.value } })}
                        className="rounded-sm font-mono text-xs pr-9" />
                      {f.secret && (
                        <button type="button" onClick={() => setRevealed((r) => ({ ...r, [f.key]: !r[f.key] }))}
                          data-testid={`toggle-secret-${f.key}`} tabIndex={-1}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {revealed[f.key] ? <EyeSlash size={15} /> : <Eye size={15} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {form.connector_id === "etsy" && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="etsy-oauth-hint">
                    {t("ecom.etsyHint")}
                  </p>
                )}
                {form.connector_id === "cgtrader" && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="cgtrader-oauth-hint">
                    {t("ecom.cgtraderHint")}
                  </p>
                )}
                {form.connector_id === "3dartix" && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="threedartix-hint">
                    {t("ecom.threedartixHint")}
                  </p>
                )}
              </div>
              <DialogFooter><Button onClick={connectStore} disabled={!FIELDS[form.connector_id].every((f) => (form.secrets[f.key] || "").trim())} className="rounded-sm" data-testid="submit-store-button">{form.connector_id === "etsy" ? t("ecom.authorize") : form.connector_id === "cgtrader" ? t("ecom.authorizeCgtrader") : t("ecom.connect")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard Icon={Storefront} slug="stores" label={t("ecom.stores")} value={stores.length} />
        <StatCard Icon={Package} slug="products" label={t("ecom.products")} value={stats.products} />
        <StatCard Icon={Receipt} slug="orders" label={t("ecom.orders")} value={stats.orders} />
        <StatCard Icon={CheckCircle} slug="connected" label={t("ecom.connected")} value={stores.filter((s) => s.status === "connected").length} />
      </div>

      {/* Stores */}
      <div className="bg-card border border-border rounded-sm p-5 mb-6" data-testid="stores-list">
        <div className="overline text-muted-foreground mb-3">{t("ecom.connectedStores")}</div>
        {stores.length === 0 && <div className="text-sm text-muted-foreground py-2">{t("ecom.noStores")}</div>}
        <div className="space-y-2">
          {stores.map((s) => (
            <div key={s.id} className="flex items-center justify-between border-b border-border last:border-0 py-2" data-testid={`store-${s.id}`}>
              <div className="flex items-center gap-3">
                <Storefront size={18} className="text-primary" />
                <div><div className="text-sm font-medium">{s.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground capitalize">{s.connector_id} · {s.status}{s.last_sync_at ? ` · ${t("ecom.lastSync")} ${new Date(s.last_sync_at).toLocaleString()}` : ""}</div></div>
              </div>
              <Button size="sm" variant="outline" className="rounded-sm" disabled={syncing === s.id} onClick={() => syncStore(s.id)} data-testid={`sync-store-${s.id}`}>
                <ArrowsClockwise size={14} className={`mr-1 ${syncing === s.id ? "animate-spin" : ""}`} />{syncing === s.id ? t("ecom.syncing") : t("ecom.sync")}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Data */}
      <Tabs defaultValue="products">
        <TabsList className="rounded-sm">
          <TabsTrigger value="products" className="rounded-sm" data-testid="ecom-tab-products"><Package size={15} className="mr-1" />{t("ecom.products")}</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-sm" data-testid="ecom-tab-orders"><Receipt size={15} className="mr-1" />{t("ecom.orders")}</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-4">
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="products-table">
            <Table>
              <TableHeader><TableRow><TableHead>{t("ecom.platform")}</TableHead><TableHead>{t("ecom.title2")}</TableHead><TableHead>SKU</TableHead><TableHead>{t("ecom.price")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {products.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-8">{t("ecom.noProducts")}</TableCell></TableRow>}
                {products.map((p) => (
                  <TableRow key={`${p.platform}-${p.external_id}`} data-testid={`product-${p.external_id}`}>
                    <TableCell><Badge variant="secondary" className="rounded-sm text-[10px] capitalize">{p.platform}</Badge></TableCell>
                    <TableCell className="text-sm font-medium">{p.title}</TableCell>
                    <TableCell className="font-mono text-xs">{p.sku || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{p.price || "—"}</TableCell>
                    <TableCell className="text-xs">{p.status}</TableCell>
                    <TableCell>{p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-primary"><ArrowSquareOut size={14} /></a>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="orders-table">
            <Table>
              <TableHeader><TableRow><TableHead>{t("ecom.platform")}</TableHead><TableHead>{t("ecom.number")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("ecom.total")}</TableHead><TableHead>{t("ecom.customer")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">{t("ecom.noOrders")}</TableCell></TableRow>}
                {orders.map((o) => (
                  <TableRow key={`${o.platform}-${o.external_id}`} data-testid={`order-${o.external_id}`}>
                    <TableCell><Badge variant="secondary" className="rounded-sm text-[10px] capitalize">{o.platform}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{o.number}</TableCell>
                    <TableCell className="text-xs">{o.status}</TableCell>
                    <TableCell className="font-mono text-xs">{o.total} {o.currency}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.customer || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
